import { api } from "@/lib/api";
import type { ZapisZaSlanje } from "@/lib/red";

/**
 * Hours as the server holds them, and how one entry is made on the device.
 *
 * Reading was online-only until the first physical test, on the reasoning that
 * a cached answer could let a row claim "Potvrđeno" for an entry still sitting
 * in the queue. The test showed the opposite failure to be the expensive one:
 * with no signal the screen fell back to an empty list and offered hours that
 * were already recorded, and the duplicates it produced were refused and left
 * stuck on the phone.
 *
 * The answer is now kept on the device by `lib/poslano` and the queue is shown
 * over the top of it, which was the concern in the first place (O-19). This
 * module stays what it was: the server, asked directly.
 */

export interface ZapisSati {
  id: string;
  client_uuid: string;
  radnik_id: string;
  radnik: string | null;
  gradiliste_id: string;
  gradiliste: string | null;
  datum: string;
  tip_zapisa: "rad" | "nenazocnost";
  vrsta_nenazocnosti: string | null;
  pocetak: string | null;
  kraj: string | null;
  zastoji_min: number;
  teren: boolean;
  ukupno_sati: number;
  status: "zaprimljen" | "treba_pregled" | "odobren" | "storniran";
  razlog_pregleda: string | null;
}

export async function dohvatiSate(
  gradilisteId: string,
  datum: string,
  signal?: AbortSignal,
): Promise<ZapisSati[]> {
  const response = await api.get<ZapisSati[]>("/sati", {
    params: { gradiliste_id: gradilisteId, datum },
    signal,
  });
  return response.data;
}

/** The absences the payroll module distinguishes, in the order people pick. */
export const NENAZOCNOSTI: { vrijednost: string; naziv: string }[] = [
  { vrijednost: "godisnji", naziv: "Godišnji odmor" },
  { vrijednost: "bolovanje", naziv: "Bolovanje" },
  { vrijednost: "blagdan", naziv: "Blagdan" },
  { vrijednost: "placeni_dopust", naziv: "Plaćeni dopust" },
  { vrijednost: "neplaceni_dopust", naziv: "Neplaćeni dopust" },
  { vrijednost: "ostalo", naziv: "Ostalo" },
];

export interface NacrtZapisa {
  radnikId: string;
  gradilisteId: string;
  datum: string;
  pocetak: string;
  kraj: string;
  zastojiMin: number;
  teren: boolean;
  nenazocnost: string | null;
}

/**
 * Turn what the screen holds into the entry that goes in the queue.
 *
 * The two device-side fields matter more than they look. `client_uuid` is the
 * idempotency key and is generated **here**, before the entry is stored, so
 * that a retry of any kind carries the same one. `unio_korisnik_id` is who is
 * signed in **now**: deriving the author from the token at send time would
 * credit the entry to whoever happens to be signed in when the signal returns,
 * which on a shared site phone is somebody else (ADR-0004 §2.7).
 */
export function nacrtUZapis(
  nacrt: NacrtZapisa,
  unioKorisnikId: string | null,
): ZapisZaSlanje {
  const nenazocnost = nacrt.nenazocnost;
  return {
    client_uuid: crypto.randomUUID(),
    radnik_id: nacrt.radnikId,
    gradiliste_id: nacrt.gradilisteId,
    datum: nacrt.datum,
    tip_zapisa: nenazocnost ? "nenazocnost" : "rad",
    vrsta_nenazocnosti: nenazocnost,
    // An absence has no shift. Sending 07:00-15:00 with it would put eight
    // hours of work and a day of leave on the same day.
    pocetak: nenazocnost ? null : nacrt.pocetak,
    kraj: nenazocnost ? null : nacrt.kraj,
    zastoji_min: nenazocnost ? 0 : nacrt.zastojiMin,
    teren: nenazocnost ? false : nacrt.teren,
    unio_korisnik_id: unioKorisnikId,
    unio_vrijeme: new Date().toISOString(),
  };
}
